import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CITY_MAP } from "./cities";
import { SITE } from "./config";
import Seo from "./Seo";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Clock, ArrowRight, CheckCircle2, Truck, Recycle, Scale } from "lucide-react";

const MATERIALS_LIST = [
  { name: "Gravats", desc: "béton, briques, parpaings, tuiles, carrelage" },
  { name: "Plâtre & placo", desc: "plaques de plâtre, BA13, faux plafonds" },
  { name: "Mélange bitumineux", desc: "enrobés, fraisat, croûtes d'asphalte" },
  { name: "Déchets verts", desc: "tontes, branches, feuilles, taille de haies" },
  { name: "Souches & troncs", desc: "souches d'arbres, troncs entiers, élagage" },
  { name: "Bois", desc: "palettes, panneaux, cagettes, bois non traité" },
  { name: "DIB", desc: "cartons, plastiques, mélanges non dangereux" },
  { name: "Pneus VL & PL", desc: "pneumatiques véhicules légers et poids lourds" },
];

export default function CityPage() {
  const { slug } = useParams();
  const city = CITY_MAP[slug];
  if (!city) return <Navigate to="/" replace />;

  const url = `https://fbenvironnement.fr/recyclage/${city.slug}`;
  const title = `Recyclage à ${city.name} — Centre FB Environnement (13)`;
  const description = `Centre de recyclage agréé pour les pros de ${city.name}. Gravats, plâtre, déchets verts, bois, DIB — ${city.distanceKm} km, ${city.drivingTime} de route. Lun-Ven 7h-17h non-stop.`;

  return (
    <div>
      <Seo title={title} description={description} path={`/recyclage/${city.slug}`} />
      {/* Breadcrumb JSON-LD pour la SERP */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fbenvironnement.fr/" },
            { "@type": "ListItem", position: 2, name: `Recyclage à ${city.name}`, item: url },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Centre de recyclage et valorisation de déchets professionnels",
          areaServed: { "@type": "City", name: city.name, postalCode: city.postalCode, addressCountry: "FR" },
          provider: {
            "@type": "LocalBusiness",
            name: "FB Environnement",
            address: {
              "@type": "PostalAddress",
              streetAddress: "30 chemin de la Carrère",
              addressLocality: "Saint-Victoret",
              postalCode: "13730",
              addressCountry: "FR",
            },
            telephone: "+33783481572",
          },
          url,
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav aria-label="breadcrumb" className="text-sm mb-4 text-stone-200">
            <Link to="/" className="hover:text-[#9DCB2E]">Accueil</Link>
            <span className="mx-2 text-stone-400">/</span>
            <span className="text-white font-bold">Recyclage à {city.name}</span>
          </nav>
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">
            Pros à {city.name}
          </span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">
            Centre de recyclage pour les pros de {city.name}
          </h1>
          <p className="text-stone-100 text-lg mt-4 max-w-2xl">
            {city.intro}
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="inline-flex items-center gap-2 bg-[#9DCB2E] hover:bg-[#86b323] text-[#0f3a18] font-black px-5 py-3 rounded-full active:scale-95 transition-all shadow-lg shadow-[#9DCB2E]/30"
            >
              <Phone className="w-4 h-4" strokeWidth={3} /> {SITE.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-5 py-3 rounded-full active:scale-95 transition-all hover:bg-stone-100"
            >
              Demander un devis <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      {/* Infos pratiques */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-3 gap-5">
        <div className="bg-white border border-stone-100 rounded-3xl p-5">
          <MapPin className="w-7 h-7 text-[#1F6B2E] mb-3" strokeWidth={2.5} />
          <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-1">Distance</div>
          <div className="font-heading font-black text-2xl">{city.distanceKm} km</div>
          <div className="text-sm text-stone-600 mt-1">{city.drivingTime} de {city.name}</div>
        </div>
        <div className="bg-white border border-stone-100 rounded-3xl p-5">
          <Clock className="w-7 h-7 text-[#1F6B2E] mb-3" strokeWidth={2.5} />
          <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-1">Horaires</div>
          <div className="font-heading font-black text-base">Lun-Ven 7h-17h</div>
          <div className="text-sm text-stone-600 mt-1">Non-stop · Sans rendez-vous</div>
        </div>
        <div className="bg-white border border-stone-100 rounded-3xl p-5">
          <Truck className="w-7 h-7 text-[#1F6B2E] mb-3" strokeWidth={2.5} />
          <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-1">Accès depuis {city.name}</div>
          <div className="text-sm text-stone-700 leading-snug">{city.accessTip}</div>
        </div>
      </section>

      {/* Matières acceptées */}
      <section className="bg-stone-50 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading font-black text-3xl text-stone-900">
            Ce qu&apos;on recycle pour les pros de {city.name}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {MATERIALS_LIST.map((m) => (
              <div key={m.name} className="bg-white border border-stone-100 rounded-2xl p-5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#1F6B2E]" strokeWidth={2.8} />
                  <h3 className="font-heading font-black text-lg">{m.name}</h3>
                </div>
                <p className="text-sm text-stone-600 mt-2">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading font-black text-3xl text-stone-900">
          Comment ça marche depuis {city.name}
        </h2>
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {[
            { icon: Truck, t: "Vous arrivez", d: `30 chemin de la Carrère, Saint-Victoret. ${city.drivingTime} depuis ${city.name}.` },
            { icon: Scale, t: "On pèse", d: "Pesée du véhicule à l'entrée puis à la sortie." },
            { icon: Recycle, t: "On trie", d: "Vous déchargez dans la benne dédiée à chaque matière." },
            { icon: CheckCircle2, t: "On certifie", d: "Bordereau de suivi remis sur demande." },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-stone-100 rounded-3xl p-5">
              <div className="w-11 h-11 rounded-2xl bg-[#1F6B2E] text-white grid place-items-center mb-3">
                <s.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-1">Étape {i + 1}</div>
              <div className="font-heading font-black text-lg">{s.t}</div>
              <p className="text-sm text-stone-600 mt-1.5">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-gradient-to-br from-[#9DCB2E] to-[#7ba81f] rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0f3a18]">
            {city.cta}
          </h2>
          <p className="text-[#0f3a18]/80 mt-3 max-w-xl mx-auto">
            Un appel suffit pour estimer votre volume et préparer votre dépôt.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="inline-flex items-center gap-2 bg-[#0f3a18] hover:bg-[#0a2e12] text-white font-bold px-6 py-3 rounded-full active:scale-95"
            >
              <Phone className="w-4 h-4" strokeWidth={3} /> {SITE.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-6 py-3 rounded-full active:scale-95"
            >
              Devis en ligne <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
